import { hydrateAccessTokenFromStorage, refreshAccessToken } from './authUtils';
import { COMPANY_ID_HEADER, getReactAppApiUrl } from './constants';
import { cacheData, getCachedData, generateCacheKey } from './offlineSyncUtils';
import { toUserFacingHttpFailureMessage } from './userFacingHttpErrors';

export { toUserFacingHttpFailureMessage } from './userFacingHttpErrors';

/** JSON body from a failed API response (e.g. Nest `BadRequestException` object). */
export type ClientHttpErrorBody = Record<string, unknown> & {
  message?: string | string[];
  detail?: string;
};

/** Thrown by `clientAuthorizedPost` (and similar) when `response.ok` is false; carries the parsed JSON body when available. */
export class ClientHttpError extends Error {
  readonly status: number;
  readonly responseBody: ClientHttpErrorBody | undefined;

  constructor(
    message: string,
    status: number,
    responseBody?: ClientHttpErrorBody,
  ) {
    super(message);
    this.name = 'ClientHttpError';
    this.status = status;
    this.responseBody = responseBody;
  }
}

/**
 * Check if the browser is online
 */
export function isOnline(): boolean {
  return navigator.onLine;
}

/** Endpoints that must not trigger session refresh retry on 401. */
function isAuthTokenEndpoint(endpoint: string): boolean {
  const clean = endpoint.replace(/^\/+/, '').toLowerCase();
  return (
    clean === 'auth/token' ||
    clean.endsWith('/auth/token') ||
    clean === 'auth/refresh-session' ||
    clean.endsWith('/auth/refresh-session') ||
    clean === 'auth/exchange' ||
    clean.endsWith('/auth/exchange')
  );
}

/**
 * Attach `X-Company-Id` when the caller supplies a company scope (replaces query `companyId`).
 */
function attachCompanyIdHeader(
  headers: Record<string, string>,
  endpoint: string,
  companyId?: string | null,
): void {
  if (!companyId) return;
  const basePath = endpoint.replace(/^\/+/, '').split('?')[0];
  if (basePath.includes('/companyId/')) return;
  if (basePath.startsWith('memberships/pending-invitations')) return;
  headers[COMPANY_ID_HEADER] = companyId;
}

/**
 * On 401, attempt to refresh the access token and retry the request once.
 * Returns the response (possibly from retry). Throws if refresh fails.
 */
async function ensureValidTokenAndRetryOn401(
  endpoint: string,
  companyId: string | null | undefined,
  doRequest: (headers: Record<string, string>) => Promise<Response>,
): Promise<Response> {
  let headers = await getAuthHeaders();
  attachCompanyIdHeader(headers, endpoint, companyId);
  let res = await doRequest(headers);
  if (res.status === 401 && !isAuthTokenEndpoint(endpoint)) {
    try {
      await refreshAccessToken();
    } catch {
      throw new Error('Session expired. Please log in again.');
    }
    headers = await getAuthHeaders();
    attachCompanyIdHeader(headers, endpoint, companyId);
    res = await doRequest(headers);
  }
  return res;
}

/**
 * Get authorization header with access token
 * Automatically refreshes token if expired
 */
async function getAuthHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = {
    accept: 'application/json',
  };

  // In Auth mode, auth flows via HttpOnly cookies.
  // Keep this call to hydrate auth marker for route guards/UI state.
  await hydrateAccessTokenFromStorage();

  return headers;
}

/**
 * Get a valid access token using the same logic as REST (hydrate + refresh if needed).
 * Use this for WebSocket handshake so the token is identical to Authorization: Bearer.
 * Returns null if no token can be obtained (e.g. refresh failed).
 */
export function getValidAccessToken(): Promise<string | null> {
  // Cookie-based auth does not expose access token to JS.
  return Promise.resolve(null);
}

export interface AuthorizedFetchOptions {
  /** Endpoint path (e.g. 'chat') for 401 retry; omit for no retry. */
  endpoint?: string;
  /** If true, do not add auth headers (e.g. presigned URLs). */
  skipAuth?: boolean;
  /** Active company UUID (`X-Company-Id`); use with `endpoint` for header scoping. */
  companyId?: string | null;
}

/**
 * Authorized fetch: adds auth headers and optional 401 retry.
 * Use this instead of global fetch for all API and upload/download requests.
 * Use skipAuth: true for presigned URLs that must not include Authorization.
 */
export async function authorizedFetch(
  url: string,
  init: RequestInit = {},
  options: AuthorizedFetchOptions = {},
): Promise<Response> {
  if (options.skipAuth) {
    return fetch(url, { ...init, credentials: 'include' });
  }
  const authHeaders = await getAuthHeaders();
  const existingHeaders =
    init.headers instanceof Headers
      ? Object.fromEntries(init.headers.entries())
      : ((init.headers as Record<string, string> | undefined) ?? {});
  const mergedHeaders = { ...authHeaders, ...existingHeaders };
  if (options.endpoint) {
    attachCompanyIdHeader(mergedHeaders, options.endpoint, options.companyId);
  }
  let res = await fetch(url, {
    ...init,
    headers: mergedHeaders,
    credentials: 'include',
  });
  if (
    res.status === 401 &&
    options.endpoint &&
    !isAuthTokenEndpoint(options.endpoint)
  ) {
    try {
      await refreshAccessToken();
    } catch {
      throw new Error('Session expired. Please log in again.');
    }
    const auth2 = await getAuthHeaders();
    const merged2 = { ...auth2, ...existingHeaders };
    if (options.endpoint) {
      attachCompanyIdHeader(merged2, options.endpoint, options.companyId);
    }
    res = await fetch(url, {
      ...init,
      headers: merged2,
      credentials: 'include',
    });
  }
  return res;
}

/**
 * Builds request URL with query parameters. Company scope is sent via `X-Company-Id`, not the query string.
 */
function buildUrlWithQueryParams(
  endpoint: string,
  queryParams?: Record<string, string>,
): string {
  const cleanEndpoint = endpoint.replace(/^\/+/, '');

  const [basePath, existingQuery] = cleanEndpoint.split('?');
  let url = `${getReactAppApiUrl()}/${basePath}`;

  const params = new URLSearchParams();

  if (existingQuery) {
    const existingParams = new URLSearchParams(existingQuery);
    existingParams.forEach((value, key) => {
      params.append(key, value);
    });
  }

  if (queryParams) {
    Object.entries(queryParams).forEach(([key, value]) => {
      if (value) {
        params.append(key, value);
      }
    });
  }

  const queryString = params.toString();
  if (queryString) {
    url += `?${queryString}`;
  }

  return url;
}

export const clientAuthorizedGet = async <T>(
  endpoint: string,
  queryParams?: Record<string, string>,
  companyId?: string | null,
): Promise<T> => {
  const cacheKey = generateCacheKey(endpoint, queryParams, companyId);

  // If offline, try to serve from cache
  if (!isOnline()) {
    const cached = (await getCachedData(cacheKey)) as T | null;
    if (cached) {
      console.log(`Serving offline data for: ${endpoint}`);
      return cached;
    }
    throw new Error(
      'Offline: No cached data available for this request. Please connect to the internet.',
    );
  }

  const url = buildUrlWithQueryParams(endpoint, queryParams);

  try {
    const res = await ensureValidTokenAndRetryOn401(
      endpoint,
      companyId,
      (headers) =>
        fetch(url, { method: 'GET', headers, credentials: 'include' }),
    );

    if (!res.ok) {
      let errorMessage = 'An error occurred while processing your request.';
      try {
        const text = await res.text();
        if (text && text.trim() !== '') {
          const resBody = JSON.parse(text) as {
            detail?: string;
            message?: string | string[];
          };
          if (resBody?.detail && typeof resBody.detail === 'string') {
            errorMessage = resBody.detail;
          } else if (resBody?.message) {
            if (typeof resBody.message === 'string') {
              errorMessage = resBody.message;
            } else if (
              Array.isArray(resBody.message) &&
              resBody.message.length > 0
            ) {
              errorMessage = resBody.message.join(', ');
            }
          }
        }
      } catch {
        // If response is not JSON, use status text
        errorMessage = res.statusText || errorMessage;
      }
      throw new Error(toUserFacingHttpFailureMessage(res.status, errorMessage));
    }

    const text = await res.text();
    if (!text || text.trim() === '') {
      void cacheData(cacheKey, null);
      return null as T;
    }
    const resBody = JSON.parse(text) as T;

    // Success! Cache the data for offline use
    void cacheData(cacheKey, resBody);

    return resBody;
  } catch (error) {
    // If it's a network error that happened even though isOnline() was true
    // (e.g. DNS failure, or connection dropped just now)
    if (error instanceof TypeError) {
      const cached = (await getCachedData(cacheKey)) as T | null;
      if (cached) {
        console.log(`Network failed, serving offline cache for: ${endpoint}`);
        return cached;
      }
    }

    // Handle network errors (including CORS, connection refused, etc.)
    if (error instanceof TypeError) {
      if (
        error.message === 'Failed to fetch' ||
        error.message.includes('fetch')
      ) {
        throw new Error(
          'Network error: Unable to connect to the server. Please check your connection and try again.',
          { cause: error },
        );
      }
    }
    // Re-throw other errors (including Error instances we created)
    if (error instanceof Error) {
      throw error;
    }
    // Handle any other unexpected errors
    throw new Error(
      'An unexpected error occurred while processing your request.',
      { cause: error },
    );
  }
};

/**
 * Public GET request without authentication (e.g. for landing/sections).
 * Does not use auth headers or offline cache. Pass `companyId` to set `X-Company-Id`.
 */
export const clientPublicGet = async <T>(
  endpoint: string,
  companyId?: string | null,
): Promise<T> => {
  const baseUrl = getReactAppApiUrl().replace(/\/+$/, '');
  const path = endpoint.replace(/^\/+/, '');
  const url = path ? `${baseUrl}/${path}` : baseUrl;

  const headers: Record<string, string> = { accept: 'application/json' };
  attachCompanyIdHeader(headers, endpoint, companyId ?? undefined);

  const res = await fetch(url, {
    method: 'GET',
    headers,
  });

  if (!res.ok) {
    let errorMessage = 'An error occurred while loading.';
    try {
      const resBody = (await res.json()) as {
        detail?: string;
        message?: string | string[];
      };
      if (resBody?.detail && typeof resBody.detail === 'string') {
        errorMessage = resBody.detail;
      } else if (resBody?.message) {
        errorMessage = Array.isArray(resBody.message)
          ? resBody.message.join(', ')
          : resBody.message;
      }
    } catch {
      errorMessage = res.statusText || errorMessage;
    }
    throw new Error(toUserFacingHttpFailureMessage(res.status, errorMessage));
  }

  return (await res.json()) as T;
};

export const clientAuthorizedPost = async <T>(
  endpoint: string,
  data?: Record<string, unknown>,
  companyId?: string | null,
): Promise<T> => {
  try {
    const url = buildUrlWithQueryParams(endpoint);
    const res = await ensureValidTokenAndRetryOn401(
      endpoint,
      companyId,
      async (headers) => {
        headers['Content-Type'] = 'application/json';
        return fetch(url, {
          method: 'POST',
          headers,
          credentials: 'include',
          ...(!data ? {} : { body: JSON.stringify(data) }),
        });
      },
    );

    if (!res.ok) {
      let errorMessage = 'An error occurred while processing your request.';
      let parsedBody: ClientHttpErrorBody | undefined;
      try {
        parsedBody = (await res.json()) as ClientHttpErrorBody;
        if (
          parsedBody &&
          parsedBody.detail &&
          typeof parsedBody.detail === 'string'
        ) {
          errorMessage = parsedBody.detail;
        } else if (parsedBody && parsedBody.message) {
          if (typeof parsedBody.message === 'string') {
            errorMessage = parsedBody.message;
          } else if (
            Array.isArray(parsedBody.message) &&
            parsedBody.message.length > 0
          ) {
            errorMessage = parsedBody.message.join(', ');
          }
        }
      } catch {
        errorMessage = res.statusText || errorMessage;
      }
      throw new ClientHttpError(
        toUserFacingHttpFailureMessage(res.status, errorMessage),
        res.status,
        parsedBody,
      );
    }

    const resBody = (await res.json()) as T;
    return resBody;
  } catch (error) {
    // Handle network errors (including CORS, connection refused, etc.)
    if (error instanceof TypeError) {
      if (
        error.message === 'Failed to fetch' ||
        error.message.includes('fetch')
      ) {
        throw new Error(
          'Network error: Unable to connect to the server. Please check your connection and try again.',
          { cause: error },
        );
      }
    }
    // Re-throw HTTP errors and other Error instances we created
    if (error instanceof Error) {
      throw error;
    }
    // Handle any other unexpected errors
    throw new Error(
      'An unexpected error occurred while processing your request.',
      { cause: error },
    );
  }
};

export const clientAuthorizedPatch = async <T>(
  endpoint: string,
  data?: Record<string, unknown>,
  companyId?: string | null,
): Promise<T> => {
  try {
    const url = buildUrlWithQueryParams(endpoint);
    const res = await ensureValidTokenAndRetryOn401(
      endpoint,
      companyId,
      async (headers) => {
        headers['Content-Type'] = 'application/json';
        return fetch(url, {
          method: 'PATCH',
          headers,
          credentials: 'include',
          ...(!data ? {} : { body: JSON.stringify(data) }),
        });
      },
    );

    if (!res.ok) {
      let errorMessage = 'An error occurred while processing your request.';
      try {
        const resBody = (await res.json()) as {
          detail?: string;
          message?: string | string[];
        };
        if (resBody && resBody.detail && typeof resBody.detail === 'string') {
          errorMessage = resBody.detail;
        } else if (resBody && resBody.message) {
          if (typeof resBody.message === 'string') {
            errorMessage = resBody.message;
          } else if (
            Array.isArray(resBody.message) &&
            resBody.message.length > 0
          ) {
            errorMessage = resBody.message.join(', ');
          }
        }
      } catch {
        errorMessage = res.statusText || errorMessage;
      }
      throw new Error(toUserFacingHttpFailureMessage(res.status, errorMessage));
    }

    const resBody = (await res.json()) as T;
    return resBody;
  } catch (error) {
    // Handle network errors (including CORS, connection refused, etc.)
    if (error instanceof TypeError) {
      if (
        error.message === 'Failed to fetch' ||
        error.message.includes('fetch')
      ) {
        throw new Error(
          'Network error: Unable to connect to the server. Please check your connection and try again.',
          { cause: error },
        );
      }
    }
    // Re-throw other errors (including Error instances we created)
    if (error instanceof Error) {
      throw error;
    }
    // Handle any other unexpected errors
    throw new Error(
      'An unexpected error occurred while processing your request.',
      { cause: error },
    );
  }
};

export const clientAuthorizedPut = async <T>(
  endpoint: string,
  data?: Record<string, unknown>,
  companyId?: string | null,
): Promise<T> => {
  try {
    const url = buildUrlWithQueryParams(endpoint);
    const res = await ensureValidTokenAndRetryOn401(
      endpoint,
      companyId,
      async (headers) => {
        headers['Content-Type'] = 'application/json';
        return fetch(url, {
          method: 'PUT',
          headers,
          credentials: 'include',
          ...(!data ? {} : { body: JSON.stringify(data) }),
        });
      },
    );

    if (!res.ok) {
      let errorMessage = 'An error occurred while processing your request.';
      try {
        const resBody = (await res.json()) as {
          detail?: string;
          message?: string | string[];
        };
        if (resBody && resBody.detail && typeof resBody.detail === 'string') {
          errorMessage = resBody.detail;
        } else if (resBody && resBody.message) {
          if (typeof resBody.message === 'string') {
            errorMessage = resBody.message;
          } else if (
            Array.isArray(resBody.message) &&
            resBody.message.length > 0
          ) {
            errorMessage = resBody.message.join(', ');
          }
        }
      } catch {
        errorMessage = res.statusText || errorMessage;
      }
      throw new Error(toUserFacingHttpFailureMessage(res.status, errorMessage));
    }

    const resBody = (await res.json()) as T;
    return resBody;
  } catch (error) {
    // Handle network errors (including CORS, connection refused, etc.)
    if (error instanceof TypeError) {
      if (
        error.message === 'Failed to fetch' ||
        error.message.includes('fetch')
      ) {
        throw new Error(
          'Network error: Unable to connect to the server. Please check your connection and try again.',
          { cause: error },
        );
      }
    }
    // Re-throw other errors (including Error instances we created)
    if (error instanceof Error) {
      throw error;
    }
    // Handle any other unexpected errors
    throw new Error(
      'An unexpected error occurred while processing your request.',
      { cause: error },
    );
  }
};

export const clientAuthorizedDelete = async <T>(
  endpoint: string,
  data?: Record<string, unknown>,
  companyId?: string | null,
  queryParams?: Record<string, string>,
): Promise<T | null> => {
  try {
    const url = buildUrlWithQueryParams(endpoint, queryParams);
    const res = await ensureValidTokenAndRetryOn401(
      endpoint,
      companyId,
      async (headers) => {
        headers['Content-Type'] = 'application/json';
        return fetch(url, {
          method: 'DELETE',
          headers,
          credentials: 'include',
          ...(!data ? {} : { body: JSON.stringify(data) }),
        });
      },
    );

    if (res.status === 204) {
      return null; // No content (e.g., resource deleted successfully)
    }

    if (!res.ok) {
      let errorMessage = 'An error occurred while processing your request.';
      try {
        const resBody = (await res.json()) as {
          detail?: string;
          message?: string | string[];
        };
        if (resBody && resBody.detail && typeof resBody.detail === 'string') {
          errorMessage = resBody.detail;
        } else if (resBody && resBody.message) {
          if (typeof resBody.message === 'string') {
            errorMessage = resBody.message;
          } else if (
            Array.isArray(resBody.message) &&
            resBody.message.length > 0
          ) {
            errorMessage = resBody.message.join(', ');
          }
        }
      } catch {
        errorMessage = res.statusText || errorMessage;
      }
      throw new Error(toUserFacingHttpFailureMessage(res.status, errorMessage));
    }

    // Check if response has content before parsing JSON
    const contentLength = res.headers.get('content-length');
    const contentType = res.headers.get('content-type');

    // If content-length is 0 or response doesn't have JSON content-type, return null
    if (
      contentLength === '0' ||
      (contentLength === null && !contentType?.includes('application/json'))
    ) {
      // Try to read text to confirm it's empty
      const text = await res.text();
      if (!text || text.trim() === '') {
        return null;
      }
      // If there's text but it's not JSON, try to parse it
      try {
        return JSON.parse(text) as T;
      } catch {
        return null;
      }
    }

    // Response has content, parse as JSON
    const resBody = (await res.json()) as T;
    return resBody;
  } catch (error) {
    // Handle network errors (including CORS, connection refused, etc.)
    if (error instanceof TypeError) {
      if (
        error.message === 'Failed to fetch' ||
        error.message.includes('fetch')
      ) {
        throw new Error(
          'Network error: Unable to connect to the server. Please check your connection and try again.',
          { cause: error },
        );
      }
    }
    // Re-throw other errors (including Error instances we created)
    if (error instanceof Error) {
      throw error;
    }
    // Handle any other unexpected errors
    throw new Error(
      'An unexpected error occurred while processing your request.',
      { cause: error },
    );
  }
};
